import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
  Divider,
} from '@mui/material';
import RepeatIcon from '@mui/icons-material/Repeat';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useRecurringStore } from '../../stores/root_store';
import type { FHRecurringInfo } from '../../types';
import { RecurringActions } from '../../components/RecurringActions';

// 0 = Monday to match Python's datetime.weekday()
const DAY_NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

function formatFrequency(info: FHRecurringInfo): string {
  const freq = info.frequency?.toLowerCase();
  if (freq === 'daily') return 'Daily';
  if (freq === 'weekdays' || freq === 'week days') return 'Weekdays';
  if (freq === 'weekly' && info.weekday !== null && info.weekday !== undefined) {
    return `Weekly on ${DAY_NAMES[info.weekday] || 'Unknown'}`;
  }
  if (freq === 'weekly') return 'Weekly';
  if (freq === 'monthly' && info.day_of_month) return `Monthly on the ${info.day_of_month}`;
  if (freq === 'monthly') return 'Monthly';
  return info.frequency || 'Unknown';
}

function formatNextDate(dateStr: string | null): string {
  if (!dateStr) return 'Not scheduled';
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

export const RecurringListPage = observer(() => {
  const recurringStore = useRecurringStore();
  const navigate = useNavigate();
  const [showInactive, setShowInactive] = useState(false);

  useEffect(() => {
    recurringStore.fetchRecurrings(true);
  }, [recurringStore]);

  const recurrings = recurringStore.allRecurrings.filter(
    r => showInactive || r.active
  );

  const refresh = () => recurringStore.fetchRecurrings(true);

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/request')}
          >
            Requests
          </Button>
          <Typography variant="h4">Recurring Requests</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/request/new')}
        >
          New Request
        </Button>
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show inactive"
        />
      </Box>

      {recurringStore.loading && recurrings.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : recurrings.length === 0 ? (
        <Card>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <RepeatIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No recurring requests
            </Typography>
            <Typography color="text.secondary" sx={{ mb: 2 }}>
              Set up a recurring request to automatically submit tasks on a schedule.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/request/new')}
            >
              Create a Request
            </Button>
          </CardContent>
        </Card>
      ) : (
        <Grid container spacing={2}>
          {recurrings.map(rec => (
            <Grid size={{ xs: 12 }} key={rec.key}>
              <RecurringListItem recurring={rec} onUpdate={refresh} />
            </Grid>
          ))}
        </Grid>
      )}

      {recurringStore.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {recurringStore.error}
        </Alert>
      )}

      {recurringStore.hasMore && !recurringStore.loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            variant="outlined"
            onClick={() => recurringStore.fetchRecurrings()}
          >
            Load More
          </Button>
        </Box>
      )}
    </Container>
  );
});

function RecurringListItem({ recurring, onUpdate }: { recurring: FHRecurringInfo; onUpdate: () => void }) {
  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, mr: 2 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
              {recurring.title || 'Untitled'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {formatFrequency(recurring)}
            </Typography>
            {recurring.content && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  mt: 1,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {recurring.content}
              </Typography>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            {!recurring.active ? (
              <Chip label="Stopped" size="small" color="error" variant="outlined" />
            ) : recurring.skip ? (
              <Chip label="Skipping Next" size="small" color="warning" variant="outlined" />
            ) : (
              <Chip label="Active" size="small" color="success" variant="outlined" />
            )}
            {recurring.active && recurring.next_date && (
              <Typography variant="caption" color="text.secondary">
                Next: {formatNextDate(recurring.next_date)}
              </Typography>
            )}
          </Box>
        </Box>

        <Divider sx={{ my: 1.5 }} />
        <RecurringActions info={recurring} onUpdate={onUpdate} />
      </CardContent>
    </Card>
  );
}
